import type { AxiosError, AxiosResponse } from "axios";
import store from "@/store";
import { CLEAR_ROUTER_LOADER, UPDATE_ERRORMSG } from "@/store/action-types";
import { resetLoaders } from "@/utils/loaders";
import { handleNetworkError } from "./networkErrorHandler";
import { handleClassifiedError } from "./classifiedErrorHandler";
import { handleHttpStatusError } from "./httpStatusHandler";

/**
 * Main error handler that orchestrates all error handling logic
 */
export async function handleError(error: AxiosError): Promise<AxiosError | AxiosResponse> {
  try {
    resetLoaders();
    store.dispatch(CLEAR_ROUTER_LOADER);

    // Handle network/timeout errors (no response)
    if (!error.response) {
      return handleNetworkError(error);
    }

    // Handle classified errors (session, provider account, rate limit, etc.)
    const classifiedErrorResult = await handleClassifiedError(error);
    // A session-expired 401 that was silently refreshed and retried succeeds
    // here — the original caller's .then() sees a normal response, never
    // knowing a refresh happened in between.
    if (classifiedErrorResult.retriedResponse) {
      return classifiedErrorResult.retriedResponse;
    }
    if (classifiedErrorResult.handled) {
      return Promise.reject(error);
    }

    // Handle database connection limit errors
    if (isDbConnectionLimitError(error)) {
      store.dispatch(UPDATE_ERRORMSG, {
        errorMsg: "The system is busy right now. Please try again shortly.",
      });
      return Promise.reject(error);
    }

    const httpResult = handleHttpStatusError(error);
    if (httpResult.absorbed) {
      error._handled = true;
    }

    return Promise.reject(error);
  } catch (catchError) {
    if (process.env.NODE_ENV === "development") {
      console.error("Error interceptor failed:", catchError);
    }
    return Promise.reject(error);
  }
}

/**
 * Check if error is a database connection limit error
 */
function isDbConnectionLimitError(error: AxiosError): boolean {
  const responseData = error.response?.data as { message?: string } | undefined;
  const backendMessage = responseData?.message;
  return (
    typeof backendMessage === "string" &&
    backendMessage
      .toLowerCase()
      .includes("remaining connection slots are reserved for roles with the superuser attribute")
  );
}
